import { NO_LOCATION_KEY } from 'constants/general';

import type IGetLocationsCountriesResponse from 'store/slices/geoLocations/interfaces/IGetLocationsCountriesResponse';

export type TCountryLookup = Map<string, string>;
export type TCountryDetailsLookup = Map<string, IGetLocationsCountriesResponse>;

const normalizeCountryLookupKey = (value: string): string => value.trim().toLowerCase();
const COUNTRY_NAME_STOP_WORDS = new Set(['and', 'of', 'the']);

const getNormalizedCountryLookupKey = (value: string | undefined): string | null => {
  if (!value) {
    return null;
  }

  const normalizedValue = value.trim();

  if (!normalizedValue) {
    return null;
  }

  return normalizeCountryLookupKey(normalizedValue);
};

const normalizeCountryAliasKey = (value: string): string => (
  value.toLowerCase().replace(/[^a-z0-9]/g, '')
);

const getCountryAliasKeys = (
  country: IGetLocationsCountriesResponse,
): string[] => {
  const keys = new Set<string>();
  const normalizedCountryName = country.name?.trim();

  if (!normalizedCountryName) {
    return [];
  }

  const exactNameKey = getNormalizedCountryLookupKey(normalizedCountryName);
  const compactNameKey = normalizeCountryAliasKey(normalizedCountryName);
  const iso2Key = getNormalizedCountryLookupKey(country.iso2);
  const iso3Key = getNormalizedCountryLookupKey(country.iso3);
  const countryNameParts = normalizedCountryName
    .split(/[^a-z0-9]+/i)
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);
  const significantCountryNameParts = countryNameParts.filter(
    (part) => !COUNTRY_NAME_STOP_WORDS.has(part),
  );
  const significantCountryNameCompactKey = significantCountryNameParts.join('');
  const significantCountryNameInitialsKey = significantCountryNameParts
    .map((part) => part[0])
    .join('');
  const shortenedCountryNameCompactKey = significantCountryNameParts
    .slice(0, -1)
    .join('');
  const shortenedCountryNameInitialsKey = significantCountryNameParts
    .slice(0, -1)
    .map((part) => part[0])
    .join('');

  if (exactNameKey) {
    keys.add(exactNameKey);
  }

  if (compactNameKey) {
    keys.add(compactNameKey);
  }

  if (iso2Key) {
    keys.add(iso2Key);
  }

  if (iso3Key) {
    keys.add(iso3Key);
  }

  if (significantCountryNameCompactKey) {
    keys.add(significantCountryNameCompactKey);
  }

  if (significantCountryNameInitialsKey) {
    keys.add(significantCountryNameInitialsKey);
  }

  if (shortenedCountryNameCompactKey) {
    keys.add(shortenedCountryNameCompactKey);
  }

  if (shortenedCountryNameInitialsKey) {
    keys.add(shortenedCountryNameInitialsKey);
  }

  return Array.from(keys);
};

export const createCountryLookup = (
  countries?: IGetLocationsCountriesResponse[],
): TCountryLookup => {
  const lookup: TCountryLookup = new Map<string, string>();

  countries?.forEach((country) => {
    const normalizedCountryName = country.name?.trim();

    if (!normalizedCountryName) {
      return;
    }

    getCountryAliasKeys(country).forEach((key) => {
      lookup.set(key, normalizedCountryName);
    });
  });

  return lookup;
};

export const createCountryDetailsLookup = (
  countries?: IGetLocationsCountriesResponse[],
): TCountryDetailsLookup => {
  const lookup: TCountryDetailsLookup = new Map<string, IGetLocationsCountriesResponse>();

  countries?.forEach((country) => {
    const normalizedCountryName = country.name?.trim();
    const normalizedCountryCode = country.iso2?.trim();

    if (!normalizedCountryName || !normalizedCountryCode) {
      return;
    }

    getCountryAliasKeys(country).forEach((key) => {
      if (!lookup.has(key)) {
        lookup.set(key, country);
      }
    });
  });

  return lookup;
};

export const getCountryDetails = (
  country: string | undefined,
  countryDetailsLookup: TCountryDetailsLookup,
): IGetLocationsCountriesResponse | undefined => {
  if (!country) {
    return undefined;
  }

  const normalizedCountry = country.trim();

  if (!normalizedCountry) {
    return undefined;
  }

  const normalizedCountryKey = getNormalizedCountryLookupKey(normalizedCountry);
  const normalizedCountryAliasKey = normalizeCountryAliasKey(normalizedCountry);

  if (!normalizedCountryKey) {
    return undefined;
  }

  return countryDetailsLookup.get(normalizedCountryKey)
    || countryDetailsLookup.get(normalizedCountryAliasKey);
};

export const getCountryDisplayName = (
  country: string | undefined,
  countryLookup: TCountryLookup,
): string | undefined => {
  if (!country) {
    return undefined;
  }

  const normalizedCountry = country.trim();

  if (!normalizedCountry) {
    return undefined;
  }

  const normalizedCountryKey = getNormalizedCountryLookupKey(normalizedCountry);
  const normalizedCountryAliasKey = normalizeCountryAliasKey(normalizedCountry);

  if (!normalizedCountryKey) {
    return undefined;
  }

  return (
    countryLookup.get(normalizedCountryKey)
    || countryLookup.get(normalizedCountryAliasKey)
    || normalizedCountry
  );
};

export const compareCountryGroupKeys = (leftCountryKey: string, rightCountryKey: string): number => {
  if (leftCountryKey === rightCountryKey) {
    return 0;
  }

  if (leftCountryKey === NO_LOCATION_KEY) {
    return 1;
  }

  if (rightCountryKey === NO_LOCATION_KEY) {
    return -1;
  }

  return leftCountryKey.localeCompare(rightCountryKey);
};
